import { CircleAlert, Search, X } from "lucide-react";
import type { JSX } from "react";
import React from "react";
import { twMerge } from "tailwind-merge";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    icon?: JSX.Element;
    includeSearchIcon?: boolean;
    errorMessage?: string | null;
    alertMessage?: string | null;
    inputContainerClasses?: string;
    ref?: React.Ref<HTMLInputElement>;
    onEnterPress?: () => void;
    // Shows a clear button, hidden while the field is empty. Needs a placeholder to detect empty.
    onClear?: () => void;
}

const Input: React.FC<InputProps> = ({
    icon,
    className,
    id,
    includeSearchIcon,
    errorMessage,
    alertMessage,
    inputContainerClasses = "",
    ref,
    onClear,
    onEnterPress,
    ...inputProps
}) => {
    let containerClasses = twMerge("relative rounded", inputContainerClasses);
    if (errorMessage) {
        containerClasses = twMerge(containerClasses, "bg-red-100");
    } else if (alertMessage) {
        containerClasses = twMerge(containerClasses, "bg-bronze-100");
    }

    let messageIcon: JSX.Element | null = null;
    if (errorMessage) {
        messageIcon = <CircleAlert className="h-4 w-4 stroke-red-700" />;
    } else if (alertMessage) {
        messageIcon = <CircleAlert className="stroke-bronze-700 h-4 w-4" />;
    }

    let messageText: string | null = null;
    if (errorMessage) {
        messageText = errorMessage;
    } else if (alertMessage) {
        messageText = alertMessage;
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        // Call the existing onKeyDown handler
        if (inputProps.onKeyDown) {
            inputProps.onKeyDown(event);
        }

        // Then optionally call onEnterPress if it's passed in
        if (event.key === "Enter" && onEnterPress) {
            event.preventDefault();
            onEnterPress();
        }
    };

    return (
        <div className={containerClasses}>
            {includeSearchIcon && (
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                    <Search className="h-4 w-4 stroke-stone-500" />
                </div>
            )}
            {icon && <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">{icon}</div>}
            <input
                ref={ref}
                {...inputProps}
                onKeyDown={handleKeyDown}
                className={twMerge(
                    "focus:border-bronze-400 rounded border border-stone-200 px-2 py-[3px] shadow focus:outline-none",
                    includeSearchIcon && "pl-7",
                    onClear && "peer pr-6",
                    errorMessage && "border-red-200 bg-red-50",
                    className,
                )}
            />
            {onClear && (
                <X
                    className="absolute top-1/2 right-1.5 h-4 w-4 -translate-y-1/2 cursor-pointer stroke-stone-500 peer-placeholder-shown:hidden hover:stroke-stone-700"
                    onClick={onClear}
                />
            )}
            {messageText && (
                <div className="flex items-center gap-2 p-2 text-red-700">
                    {messageIcon}
                    {messageText}
                </div>
            )}
        </div>
    );
};

export default Input;
